import {
  useCallback,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import { Download, Plus, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import api from "../../../lib/api";
import { exportCrmExcel } from "../../../lib/crmExcel";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { Pagination } from "../../../components/ui/Pagination";
import {
  Table,
  type TableColumn,
} from "../../../components/ui/Table";
import { CrmRenovarPolizaModal } from "./CrmRenovarPolizaModal";
import {
  CrmEditarImportesModal,
} from "./CrmEditarImportesModal";
type TipoPoliza =
  | "ACC"
  | "PTD"
  | "KARTING_ALQUILER"
  | "RC_CIRCUITO"
  | "RC_ORGANIZADOR"
  | "RC_EMPRESA"
  | "MULTIRRIESGO_EMPRESA"
  | "RC_PENDIENTE";

interface CrmPolizaRow {
  id: number;
  id_externo: string;
  crm_cliente_id: number;
  id_cliente_externo: string;
  cliente: string;
  tipo_poliza: TipoPoliza;
  numero_poliza: string;
  aseguradora: string;
  tipo_negocio: string;
  estado: "activa" | "cancelada" | "archivada";
  avisos_habilitados: number;
  vigencia_id: number | null;
prima_neta_cents: number | null;
impuestos_cents: number | null;
recargo_cents: number | null;
consorcio_cents: number | null;
prima_total_cents: number | null;
comision_porcentaje_bps:
  number | null;
comision_cents: number | null;
  fecha_efecto: string | null;
  fecha_vencimiento: string | null;
  aviso_estado: string | null;
    importes_automaticos: boolean;
  cliente_vinculado?: boolean;
  pilotos_calculados?: number;
}

interface PolizasResponse {
  data: CrmPolizaRow[];
  total: number;
  page: number;
  totalPages: number;
}

interface ClienteOption {
  id: number;
  idExterno: string | null;
  razonSocial: string;
  clientePlataformaId: number | null;
  emailContacto: string | null;
  estadoComercial: "prospect" | "cliente" | "inactivo";
}

interface ClientesResponse {
  data: ClienteOption[];
}

interface PolizaForm {
  crmClienteId: string;
  oportunidadId: string;
  tipoPoliza: TipoPoliza;
  numeroPoliza: string;
  aseguradora: string;
  tipoNegocio: string;
  fechaEfecto: string;
  fechaVencimiento: string;
  primaNeta: string;
impuestos: string;
recargo: string;
consorcio: string;
primaTotal: string;
comisionPorcentaje: string;
  avisosHabilitados: boolean;
  emailAcceso: string;
passwordAcceso: string;
metodoPago: string;
}

const TIPOS_POLIZA: Array<{
  value: TipoPoliza;
  label: string;
  negocio: string;
}> = [
  {
    value: "ACC",
    label: "Accidentes circuito",
    negocio: "Accidentes circuito",
  },
  {
    value: "PTD",
    label: "Invalidez permanente (PTD)",
    negocio: "PTD",
  },
  {
    value: "KARTING_ALQUILER",
    label: "Accidentes flotas de kart",
    negocio: "Karting alquiler",
  },
  {
    value: "RC_CIRCUITO",
    label: "RC para circuitos",
    negocio: "RC circuitos",
  },
  {
    value: "RC_ORGANIZADOR",
    label: "RC para organizadores",
    negocio: "RC organizadores",
  },
  {
    value: "RC_EMPRESA",
    label: "RC para empresas",
    negocio: "RC empresas",
  },
  {
    value: "MULTIRRIESGO_EMPRESA",
    label: "Multirriesgo empresas",
    negocio: "Multirriesgo empresas",
  },
  {
    value: "RC_PENDIENTE",
    label: "RC pendiente de clasificar",
    negocio: "RC pendiente de clasificar",
  },
];

const EMPTY_FORM: PolizaForm = {
  crmClienteId: "",
  oportunidadId: "",
  tipoPoliza: "ACC",
  numeroPoliza: "",
  aseguradora: "",
  tipoNegocio: "Accidentes circuito",
  fechaEfecto: "",
  fechaVencimiento: "",
  primaNeta: "",
impuestos: "",
recargo: "",
consorcio: "",
primaTotal: "",
comisionPorcentaje: "",
  avisosHabilitados: true,
  emailAcceso: "",
passwordAcceso: "",
metodoPago: "tpv",
};

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return (
      error.response?.data?.error ??
      "No se pudo completar la operación"
    );
  }

  return "No se pudo completar la operación";
}

function formatDate(value: string | null): string {

  if (!value) return "—";

  const parts = value.split("-");

  if (parts.length !== 3) return value;

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function formatCents(
  value: number | null
): string {
  if (value === null) {
    return "Pendiente";
  }

  return new Intl.NumberFormat(
    "es-ES",
    {
      style: "currency",
      currency: "EUR",
    }
  ).format(value / 100);
}
function formatDateInput(value: string): string {
  const digits = value
    .replace(/\D/g, "")
    .slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(
    2,
    4
  )}/${digits.slice(4)}`;
}

function parseSpanishDate(
  value: string
): string | null {
  const match =
    /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);

  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  const date = new Date(
    Date.UTC(year, month - 1, day)
  );

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${String(year).padStart(
    4,
    "0"
  )}-${String(month).padStart(
    2,
    "0"
  )}-${String(day).padStart(2, "0")}`;
}
function calculateAnnualExpiry(
  value: string
): string {
  const isoDate = parseSpanishDate(value);

  if (!isoDate) return "";

  const [year, month, day] = isoDate
    .split("-")
    .map(Number);

  const nextYear = year + 1;

  const lastDayOfMonth = new Date(
    Date.UTC(nextYear, month, 0)
  ).getUTCDate();

  const expiryDay = Math.min(
    day,
    lastDayOfMonth
  );

  return `${String(expiryDay).padStart(
    2,
    "0"
  )}/${String(month).padStart(
    2,
    "0"
  )}/${nextYear}`;
}
function tipoLabel(tipo: TipoPoliza): string {
  return (
    TIPOS_POLIZA.find(
      (option) => option.value === tipo
    )?.label ?? tipo
  );
}

function estadoBadge(
  estado: CrmPolizaRow["estado"]
) {
  if (estado === "activa") {
    return <Badge variant="success">Activa</Badge>;
  }

  if (estado === "cancelada") {
    return <Badge variant="danger">Cancelada</Badge>;
  }

  return <Badge variant="neutral">Archivada</Badge>;
}

export function CrmPolizasTab() {
  const [searchParams] = useSearchParams();
  const [polizas, setPolizas] = useState<
    CrmPolizaRow[]
  >([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [renovacionDias, setRenovacionDias] = useState(
    () => searchParams.get("renovacion_dias") ?? ""
  );
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [listError, setListError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] =
    useState<PolizaForm>(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
    const [noticeSavingId, setNoticeSavingId] =
    useState<number | null>(null);
    const [renewPoliza, setRenewPoliza] =
    useState<CrmPolizaRow | null>(null);

  const [
  importesPoliza,
  setImportesPoliza,
] = useState<CrmPolizaRow | null>(
  null
);

    const [clienteSearch, setClienteSearch] =
    useState("");
  const [clientes, setClientes] = useState<
    ClienteOption[]
  >([]);
  const [clientesLoading, setClientesLoading] =
    useState(false);
  const [oportunidades, setOportunidades] = useState<Array<{
    id: number;
    idExterno: string | null;
    titulo: string;
    estado: "abierta" | "ganada" | "perdida";
  }>>([]);
    const selectedCliente =
  clientes.find(
    (cliente) =>
      cliente.id ===
      Number(form.crmClienteId)
  ) ?? null;

const necesitaAccesoOperativo =
  form.tipoPoliza === "ACC" &&
  selectedCliente !== null &&
  selectedCliente.clientePlataformaId === null;
  const loadPolizas = useCallback(async () => {
    setLoading(true);
    setListError("");

    try {
      const response =
        await api.get<PolizasResponse>(
          "/admin/crm/polizas",
          {
            params: {
              page,
              limit: 20,
              search: search.trim() || undefined,
              tipo_poliza:
                tipoFiltro || undefined,
              renovacion_dias:
                renovacionDias || undefined,
            },
          }
        );

      setPolizas(response.data.data);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setPolizas([]);
      setTotal(0);
      setTotalPages(1);
      setListError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [page, renovacionDias, search, tipoFiltro]);
  const exportPolizas = async () => {
    setExporting(true);
    setListError("");

    try {
      const allPolizas: CrmPolizaRow[] = [];
      let exportPage = 1;
      let exportTotalPages = 1;

      do {
        const response =
          await api.get<PolizasResponse>(
            "/admin/crm/polizas",
            {
              params: {
                page: exportPage,
                limit: 100,
              },
            }
          );

        allPolizas.push(...response.data.data);
        exportTotalPages =
          response.data.totalPages;
        exportPage += 1;
      } while (exportPage <= exportTotalPages);

      const date = new Date()
        .toISOString()
        .slice(0, 10);

      await exportCrmExcel({
        filename: `polizas-crm-${date}.xlsx`,
        sheetName: "Pólizas CRM",
        rows: allPolizas,
        columns: [
          {
            header: "ID póliza",
            key: "id_poliza",
            width: 14,
            value: (row) => row.id_externo,
          },
          {
            header: "ID cliente",
            key: "id_cliente",
            width: 14,
            value: (row) => row.id_cliente_externo,
          },
          {
            header: "Cliente",
            key: "cliente",
            width: 34,
            value: (row) => row.cliente,
          },
          {
            header: "Tipo",
            key: "tipo",
            width: 28,
            value: (row) =>
              tipoLabel(row.tipo_poliza),
          },
          {
            header: "Nº póliza",
            key: "numero_poliza",
            width: 24,
            value: (row) => row.numero_poliza,
          },
          {
            header: "Aseguradora",
            key: "aseguradora",
            width: 30,
            value: (row) => row.aseguradora,
          },
          {
            header: "Tipo de negocio",
            key: "tipo_negocio",
            width: 28,
            value: (row) => row.tipo_negocio,
          },
          {
            header: "Fecha efecto",
            key: "fecha_efecto",
            width: 16,
            value: (row) =>
              formatDate(row.fecha_efecto),
          },
          {
            header: "Vencimiento",
            key: "fecha_vencimiento",
            width: 16,
            value: (row) =>
              formatDate(row.fecha_vencimiento),
          },
          {
            header: "Prima neta (€)",
            key: "prima_neta",
            width: 18,
            value: (row) =>
              row.prima_neta_cents === null
                ? null
                : row.prima_neta_cents / 100,
            numFmt: '#,##0.00 "€"',
          },
          {
            header: "Impuestos (€)",
            key: "impuestos",
            width: 18,
            value: (row) =>
              row.impuestos_cents === null
                ? null
                : row.impuestos_cents / 100,
            numFmt: '#,##0.00 "€"',
          },
          {
            header: "Recargo (€)",
            key: "recargo",
            width: 16,
            value: (row) =>
              row.recargo_cents === null
                ? null
                : row.recargo_cents / 100,
            numFmt: '#,##0.00 "€"',
          },
          {
            header: "Consorcio (€)",
            key: "consorcio",
            width: 16,
            value: (row) =>
              row.consorcio_cents === null
                ? null
                : row.consorcio_cents / 100,
            numFmt: '#,##0.00 "€"',
          },
          {
            header: "Prima total (€)",
            key: "prima_total",
            width: 18,
            value: (row) =>
              row.prima_total_cents === null
                ? null
                : row.prima_total_cents / 100,
            numFmt: '#,##0.00 "€"',
          },
          {
            header: "Comisión (%)",
            key: "comision_porcentaje",
            width: 16,
            value: (row) =>
              row.comision_porcentaje_bps === null
                ? null
                : row.comision_porcentaje_bps / 100,
            numFmt: '0.00" %"',
          },
          {
            header: "Comisión (€)",
            key: "comision",
            width: 18,
            value: (row) =>
              row.comision_cents === null
                ? null
                : row.comision_cents / 100,
            numFmt: '#,##0.00 "€"',
          },
          {
            header: "Estado",
            key: "estado",
            width: 14,
            value: (row) => row.estado,
          },
          {
            header: "Aviso vencimiento",
            key: "avisos",
            width: 20,
            value: (row) =>
              row.avisos_habilitados === 1
                ? "Sí"
                : "No",
          },
          {
            header: "Cálculo de importes",
            key: "calculo_importes",
            width: 24,
            value: (row) =>
              row.importes_automaticos
                ? row.cliente_vinculado
                  ? "Automático"
                  : "Cliente sin vincular"
                : "Manual",
          },
          {
            header: "Pilotos calculados",
            key: "pilotos_calculados",
            width: 20,
            value: (row) =>
              row.pilotos_calculados ?? 0,
          },
        ],
      });
    } catch (error) {
      setListError(getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };
  useEffect(() => {
    const timer = window.setTimeout(
      loadPolizas,
      250
    );

    return () => window.clearTimeout(timer);
  }, [loadPolizas]);

  const loadClientes = useCallback(async () => {
    if (!modalOpen) return;

    setClientesLoading(true);

    try {
      const response =
        await api.get<ClientesResponse>(
          "/admin/crm/clientes",
          {
            params: {
              page: 1,
              limit: 100,
              estado: "activo",
              search:
                clienteSearch.trim() || undefined,
            },
          }
        );

      setClientes(response.data.data);
    } catch {
      setClientes([]);
    } finally {
      setClientesLoading(false);
    }
  }, [clienteSearch, modalOpen]);

  useEffect(() => {
    const timer = window.setTimeout(
      loadClientes,
      250
    );

    return () => window.clearTimeout(timer);
  }, [loadClientes]);

  useEffect(() => {
    if (!modalOpen || !form.crmClienteId) {
      setOportunidades([]);
      return;
    }

    let cancelled = false;
    api
      .get<{
        oportunidades: Array<{
          id: number;
          idExterno: string | null;
          titulo: string;
          estado: "abierta" | "ganada" | "perdida";
        }>;
      }>(`/admin/crm/seguimiento/empresa/${form.crmClienteId}`)
      .then((response) => {
        if (!cancelled) {
          setOportunidades(
            response.data.oportunidades.filter(
              (oportunidad) => oportunidad.estado === "abierta"
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) setOportunidades([]);
      });

    return () => {
      cancelled = true;
    };
  }, [form.crmClienteId, modalOpen]);

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setClienteSearch("");
    setClientes([]);
    setModalOpen(true);
  };

  const closeCreateModal = () => {
    if (saving) return;

    setModalOpen(false);
    setFormError("");
  };

  const setTextField = (
    field: Exclude<
      keyof PolizaForm,
      "avisosHabilitados"
    >,
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const changeTipoPoliza = (
    tipoPoliza: TipoPoliza
  ) => {
    const option = TIPOS_POLIZA.find(
      (item) => item.value === tipoPoliza
    );

    setForm((current) => ({
      ...current,
      tipoPoliza,
      tipoNegocio:
        option?.negocio ?? current.tipoNegocio,
      avisosHabilitados:
  tipoPoliza === "ACC",
    }));
  };

   const createPoliza = async () => {
    setFormError("");

        const importesIncompletos =
      form.tipoPoliza !== "ACC" &&
      [
        form.primaNeta,
        form.impuestos,
        form.recargo,
        form.consorcio,
        form.primaTotal,
        form.comisionPorcentaje,
      ].some(
        (value) => !value.trim()
      );

    if (
      !form.crmClienteId ||
      !form.numeroPoliza.trim() ||
      !form.aseguradora.trim() ||
      !form.tipoNegocio.trim() ||
      !form.fechaEfecto ||
      !form.fechaVencimiento ||
      importesIncompletos
    ) {
      setFormError(
        "Completa todos los campos obligatorios."
      );
      return;
    }

    const fechaEfectoIso =
      parseSpanishDate(form.fechaEfecto);

    const fechaVencimientoIso =
      parseSpanishDate(form.fechaVencimiento);

    if (
      !fechaEfectoIso ||
      !fechaVencimientoIso
    ) {
      setFormError(
        "Introduce las fechas con el formato DD/MM/AAAA."
      );
      return;
    }

    if (
      fechaVencimientoIso <= fechaEfectoIso
    ) {
      setFormError(
        "La fecha de vencimiento debe ser posterior a la fecha de efecto."
      );
      return;
    }
if (
  necesitaAccesoOperativo &&
  form.passwordAcceso.length < 8
) {
  setFormError(
    "La contraseña de acceso debe tener al menos 8 caracteres."
  );
  return;
}
    setSaving(true);

    try {
      await api.post("/admin/crm/polizas", {
        crm_cliente_id: Number(
          form.crmClienteId
        ),
        oportunidad_id: form.oportunidadId
          ? Number(form.oportunidadId)
          : null,
        tipo_poliza: form.tipoPoliza,
        numero_poliza: form.numeroPoliza,
        aseguradora: form.aseguradora,
        tipo_negocio: form.tipoNegocio,
        fecha_efecto: fechaEfectoIso,
        fecha_vencimiento:
          fechaVencimientoIso,
        prima_neta:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.primaNeta,
        impuestos:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.impuestos,
        recargo:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.recargo,
        consorcio:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.consorcio,
        prima_total:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.primaTotal,
        comision_porcentaje:
          form.tipoPoliza === "ACC"
            ? "20"
            : form.comisionPorcentaje,
          avisos_habilitados:
          form.avisosHabilitados,
          email_acceso:
  form.emailAcceso,
password_acceso:
  form.passwordAcceso,
metodo_pago:
  form.metodoPago,
      });

      setModalOpen(false);
      setForm(EMPTY_FORM);

      if (page !== 1) {
        setPage(1);
      } else {
        await loadPolizas();
      }
    } catch (error) {
      setFormError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const toggleAvisoVencimiento = async (
    poliza: CrmPolizaRow
  ) => {
    setNoticeSavingId(poliza.id);
    setListError("");

    try {
      await api.patch(
        `/admin/crm/polizas/${poliza.id}`,
        {
          avisos_habilitados:
            poliza.avisos_habilitados !== 1,
        }
      );

      await loadPolizas();
    } catch (error) {
      setListError(getErrorMessage(error));
    } finally {
      setNoticeSavingId(null);
    }
  };

  const columns: TableColumn<CrmPolizaRow>[] = [
    {
      key: "id_externo",
      header: "ID póliza",
      render: (poliza) => (
        <span className="font-semibold text-[#1A3AB8]">
          {poliza.id_externo}
        </span>
      ),
    },
    {
      key: "cliente",
      header: "Cliente",
      render: (poliza) => (
        <div>
          <div className="font-medium">
            {poliza.cliente}
          </div>
          <div className="text-xs text-gray-500">
            {poliza.id_cliente_externo}
          </div>
        </div>
      ),
    },
    {
      key: "tipo_poliza",
      header: "Tipo",
      render: (poliza) =>
        tipoLabel(poliza.tipo_poliza),
    },
    {
      key: "numero_poliza",
      header: "Nº póliza",
    },
    {
      key: "aseguradora",
      header: "Aseguradora",
    },
    {
      key: "fecha_efecto",
      header: "Fecha efecto",
      render: (poliza) =>
        formatDate(poliza.fecha_efecto),
    },
    {
      key: "fecha_vencimiento",
      header: "Vencimiento",
      render: (poliza) =>
        formatDate(poliza.fecha_vencimiento),
    },
    {
  key: "prima_total_cents",
  header: "Prima total",
  render: (poliza) =>
    formatCents(
      poliza.prima_total_cents
    ),
},
{
  key: "comision_cents",
  header: "Comisión",
  render: (poliza) => (
    <div>
      <div>
        {formatCents(
          poliza.comision_cents
        )}
      </div>

      {poliza.comision_porcentaje_bps !==
        null && (
        <div className="text-xs text-gray-500">
          {poliza.comision_porcentaje_bps /
            100}
          %
        </div>
      )}
    </div>
  ),
},
    {
      key: "estado",
      header: "Estado",
      render: (poliza) =>
        estadoBadge(poliza.estado),
    },
        {
      key: "acciones",
      header: "Acciones",
      render: (poliza) => (
        <div className="flex flex-wrap gap-2">
          {poliza.tipo_poliza === "ACC" && (
            <Button
              variant="secondary"
              size="sm"
              loading={
                noticeSavingId === poliza.id
              }
              onClick={() =>
                void toggleAvisoVencimiento(
                  poliza
                )
              }
            >
              {poliza.avisos_habilitados === 1
                ? "Desactivar aviso"
                : "Activar aviso"}
            </Button>
          )}

            {poliza.importes_automaticos ? (
            <span
              className={
                poliza.cliente_vinculado
                  ? "inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800"
                  : "inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800"
              }
            >
              {poliza.cliente_vinculado
                ? "Importes automáticos"
                : "Cliente sin vincular"}
            </span>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              disabled={
                poliza.vigencia_id === null
              }
              onClick={() =>
                setImportesPoliza(poliza)
              }
            >
              {poliza.prima_total_cents === null
                ? "Añadir importes"
                : "Editar importes"}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setRenewPoliza(poliza)
            }
          >
            Renovar
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Pólizas CRM
          </h2>

          <p className="mt-1 text-sm text-gray-600">
            Un mismo cliente puede tener varias pólizas.
            Cada póliza recibe su propio ID correlativo.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
  <Button
    variant="secondary"
    onClick={exportPolizas}
    loading={exporting}
  >
    <Download className="h-4 w-4" />
    Exportar Excel
  </Button>

  <Button onClick={openCreateModal}>
    <Plus className="h-4 w-4" />
    Nueva póliza
  </Button>
</div>
      </div>

      <div className="grid gap-3 rounded-lg bg-gray-50 p-4 lg:grid-cols-[1fr_240px_240px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

          <Input
            aria-label="Buscar pólizas"
            placeholder="Buscar por ID, cliente, nº de póliza o aseguradora"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <select
          aria-label="Filtrar por tipo de póliza"
          value={tipoFiltro}
          onChange={(event) => {
            setTipoFiltro(event.target.value);
            setPage(1);
          }}
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
        >
          <option value="">Todos los tipos</option>

          {TIPOS_POLIZA.map((tipo) => (
            <option
              key={tipo.value}
              value={tipo.value}
            >
              {tipo.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Filtrar por vencimiento"
          value={renovacionDias}
          onChange={(event) => {
            setRenovacionDias(event.target.value);
            setPage(1);
          }}
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
        >
          <option value="">Cualquier vencimiento</option>
          <option value="30">Renovación: 30 días</option>
          <option value="60">Renovación: 60 días</option>
          <option value="90">Renovación: 90 días</option>
        </select>
      </div>

      {listError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {listError}
        </div>
      )}

      <div className="text-sm text-gray-600">
        {total === 1
          ? "1 póliza"
          : `${total} pólizas`}
      </div>

      <Table
        columns={columns}
        data={polizas}
        loading={loading}
        emptyMessage="Todavía no hay pólizas en la cartera CRM"
        floatingHorizontalScrollbar
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <Modal
        isOpen={modalOpen}
        onClose={closeCreateModal}
        title="Nueva póliza CRM"
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeCreateModal}
              disabled={saving}
            >
              Cancelar
            </Button>

            <Button
              onClick={createPoliza}
              loading={saving}
            >
              Crear póliza
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            El ID de póliza se generará automáticamente y
            será independiente del ID de empresa. Si seleccionas
            un prospect, su misma ficha evolucionará a cliente.
          </div>

          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {formError}
            </div>
          )}

          <div className="space-y-3">
            <Input
              label="Buscar cliente"
              placeholder="Escribe el nombre, CIF o ID del cliente"
              value={clienteSearch}
              onChange={(event) =>
                setClienteSearch(event.target.value)
              }
            />

            <div>
              <label
                htmlFor="crm-cliente"
                className="mb-1 block text-sm font-medium text-[#2C2C2C]"
              >
                Cliente *
              </label>

              <select
                id="crm-cliente"
                value={form.crmClienteId}
                onChange={(event) =>
                  setTextField(
                    "crmClienteId",
                    event.target.value
                  )
                }
                className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
              >
                <option value="">
                  {clientesLoading
                    ? "Buscando clientes…"
                    : "Selecciona un cliente"}
                </option>

                {clientes.map((cliente) => (
                  <option
                    key={cliente.id}
                    value={cliente.id}
                  >
                    {cliente.idExterno ?? "Prospect sin ID de cliente"} —{" "}
                    {cliente.razonSocial}
                    {cliente.estadoComercial === "prospect"
                      ? " (Prospect)"
                      : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="crm-oportunidad"
                className="mb-1 block text-sm font-medium text-[#2C2C2C]"
              >
                Oportunidad ganada
              </label>
              <select
                id="crm-oportunidad"
                value={form.oportunidadId}
                onChange={(event) =>
                  setTextField("oportunidadId", event.target.value)
                }
                disabled={!form.crmClienteId}
                className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:bg-gray-100"
              >
                <option value="">No vincular una oportunidad</option>
                {oportunidades.map((oportunidad) => (
                  <option key={oportunidad.id} value={oportunidad.id}>
                    {oportunidad.idExterno} — {oportunidad.titulo}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Si eliges una, se marcará como ganada y quedará vinculada a esta póliza.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="crm-tipo-poliza"
                className="mb-1 block text-sm font-medium text-[#2C2C2C]"
              >
                Tipo de póliza *
              </label>

              <select
                id="crm-tipo-poliza"
                value={form.tipoPoliza}
                onChange={(event) =>
                  changeTipoPoliza(
                    event.target.value as TipoPoliza
                  )
                }
                className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
              >
                {TIPOS_POLIZA.map((tipo) => (
                  <option
                    key={tipo.value}
                    value={tipo.value}
                  >
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Nº de póliza de la aseguradora *"
              value={form.numeroPoliza}
              onChange={(event) =>
                setTextField(
                  "numeroPoliza",
                  event.target.value
                )
              }
            />

            <Input
              label="Aseguradora *"
              value={form.aseguradora}
              onChange={(event) =>
                setTextField(
                  "aseguradora",
                  event.target.value
                )
              }
            />

            <Input
              label="Tipo de negocio *"
              value={form.tipoNegocio}
              onChange={(event) =>
                setTextField(
                  "tipoNegocio",
                  event.target.value
                )
              }
            />

            <Input
  label="Fecha de efecto *"
  type="text"
  inputMode="numeric"
  placeholder="DD/MM/AAAA"
  maxLength={10}
  helpText="Ejemplo: 01/10/2026"
  value={form.fechaEfecto}
  onChange={(event) => {
  const fecha = formatDateInput(
    event.target.value
  );

  setForm((current) => ({
    ...current,
    fechaEfecto: fecha,
    fechaVencimiento:
      calculateAnnualExpiry(fecha),
  }));
}}
/>

<Input
  label="Fecha de vencimiento *"
  type="text"
  inputMode="numeric"
  placeholder="DD/MM/AAAA"
  maxLength={10}
  helpText="Ejemplo: 01/10/2027"
  value={form.fechaVencimiento}
  onChange={(event) =>
    setTextField(
      "fechaVencimiento",
      formatDateInput(event.target.value)
    )
  }
/>
          {form.tipoPoliza === "ACC" ? (
  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 md:col-span-2">
    <p className="font-semibold text-blue-900">
      Importes automáticos
    </p>
    <p className="mt-1 text-sm text-blue-800">
      La prima neta, los impuestos, el consorcio,
      el recargo, la prima total y la comisión se
      calcularán con los pilotos pagados durante
      la vigencia de esta póliza.
    </p>
  </div>
) : (
  <>
          <Input
  label="Prima neta (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.primaNeta}
  onChange={(event) =>
    setTextField(
      "primaNeta",
      event.target.value
    )
  }
/>

<Input
  label="Impuestos (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.impuestos}
  onChange={(event) =>
    setTextField(
      "impuestos",
      event.target.value
    )
  }
/>

<Input
  label="Recargo (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.recargo}
  onChange={(event) =>
    setTextField(
      "recargo",
      event.target.value
    )
  }
/>

<Input
  label="Consorcio (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.consorcio}
  onChange={(event) =>
    setTextField(
      "consorcio",
      event.target.value
    )
  }
/>

<Input
  label="Prima total (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.primaTotal}
  onChange={(event) =>
    setTextField(
      "primaTotal",
      event.target.value
    )
  }
/>

<Input
  label="Comisión sobre prima neta (%) *"
  type="number"
  min="0"
  max="100"
  step="0.01"
  value={form.comisionPorcentaje}
  onChange={(event) =>
    setTextField(
      "comisionPorcentaje",
      event.target.value
    )
  }
/>
            </>
)}
</div>
{necesitaAccesoOperativo && (
  <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
    <div>
      <h3 className="font-semibold text-blue-900">
        Crear acceso a la plataforma
      </h3>

      <p className="mt-1 text-sm text-blue-800">
        Este cliente todavía solo existe en el CRM.
        Al guardar la póliza aparecerá también en
        Clientes.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2">
      <Input
        label="Email de acceso"
        type="email"
        placeholder={
          selectedCliente?.emailContacto ??
          "Email de contacto"
        }
        value={form.emailAcceso}
        onChange={(event) =>
          setTextField(
            "emailAcceso",
            event.target.value
          )
        }
      />

      <Input
        label="Contraseña de acceso *"
        type="password"
        value={form.passwordAcceso}
        onChange={(event) =>
          setTextField(
            "passwordAcceso",
            event.target.value
          )
        }
      />

      <label className="block md:col-span-2">
        <span className="mb-1 block text-sm font-medium text-gray-700">
          Método de pago *
        </span>

        <select
          value={form.metodoPago}
          onChange={(event) =>
            setTextField(
              "metodoPago",
              event.target.value
            )
          }
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
        >
          <option value="tpv">TPV</option>
          <option value="transferencia">
            Transferencia
          </option>
        </select>
      </label>
    </div>
  </div>
)}
          {form.tipoPoliza === "ACC" && (
            <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-4">
              <input
                type="checkbox"
                checked={form.avisosHabilitados}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    avisosHabilitados:
                      event.target.checked,
                  }))
                }
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#1A3AB8]"
              />

              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Habilitar aviso de vencimiento
                </span>

                <span className="mt-1 block text-sm text-gray-500">
                  La plataforma enviará el aviso 45 días antes del vencimiento cuando este control esté activado.
                </span>
              </span>
            </label>
          )}
        </div>
      </Modal>
            <CrmRenovarPolizaModal
                poliza={
          renewPoliza
            ? {
                id: renewPoliza.id,
                idExterno:
                  renewPoliza.id_externo,
                cliente: renewPoliza.cliente,
                tipoPoliza: renewPoliza.tipo_poliza,
                fechaVencimiento:
                  renewPoliza.fecha_vencimiento,
              }
            : null
        }
        onClose={() => setRenewPoliza(null)}
        onSaved={async () => {
          setRenewPoliza(null);
          await loadPolizas();
        }}
      />
    <CrmEditarImportesModal
  poliza={
    importesPoliza
      ? {
          vigenciaId:
            importesPoliza.vigencia_id,
          idExterno:
            importesPoliza.id_externo,
          cliente:
            importesPoliza.cliente,
          primaNetaCents:
            importesPoliza
              .prima_neta_cents,
          impuestosCents:
            importesPoliza
              .impuestos_cents,
          recargoCents:
            importesPoliza
              .recargo_cents,
          consorcioCents:
            importesPoliza
              .consorcio_cents,
          primaTotalCents:
            importesPoliza
              .prima_total_cents,
          comisionPorcentajeBps:
            importesPoliza
              .comision_porcentaje_bps,
        }
      : null
  }
  onClose={() =>
    setImportesPoliza(null)
  }
  onSaved={async () => {
    setImportesPoliza(null);
    await loadPolizas();
  }}
/>
    </div>
  );
}
